import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { concatWith, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { StringAnyMap, StringStringMap } from 'src/app/core/models/common.models';
import { FilterDataSource, FilterSearchParams } from 'src/app/core/store/reducers/filters';
import { BaseMultiSelectComponent } from 'src/app/shared/components/craft-multi-select/craft-multi-select.component';

@Component({
  selector: 'craft-filter-select',
  templateUrl: './craft-filter-select.component.html',
  styleUrls: ['./craft-filter-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CraftFilterSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CraftFilterSelectComponent extends BaseMultiSelectComponent implements OnInit, OnDestroy {
  @Input() public items: ReadonlyArray<any> | Readonly<StringAnyMap>;
  @Input() public selectedIds?: string[] = [];
  @Input() public buttonText: StringStringMap;
  @Input() public dataSource: FilterDataSource<any>;
  @Input() public searchParams?: FilterSearchParams;
  @Input() public hideRemoveFilter?: boolean;

  @Output() public apply = new EventEmitter<string[]>();
  @Output() public remove = new EventEmitter<void>();

  private isApplied = false;

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  @HostBinding('class.active')
  public get isAnySelect() {
    return (this.selectedIds?.length || 0) > 0;
  }

  public ngOnInit() {
    if (this.dataSource) {
      this.setItems([], this.selectedIds);

      this.filteredItems = of('').pipe(
        concatWith(this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged())),
        switchMap((searchText) => this.filterSource(searchText)),
        tap(() => {
          setTimeout(() => {
            this.searchInput.nativeElement?.focus?.();
          }, 0);
        }),
      );
    }
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public clearSelected(e: MouseEvent) {
    e.stopPropagation();
    this.selectedSet.clear();
    this.applyFilters();
  }

  public removeFilter(e: MouseEvent) {
    e.stopPropagation();
    this.remove.emit();
  }

  public clearAndApply() {
    this.selectedSet.clear();
    this.applyFilters();
  }

  public applyFilters() {
    this.isApplied = true;
    this.selectedIds = Array.from(this.selectedSet.keys());

    this.apply.next(this.selectedIds);
    this.onChange(this.selectedIds);
  }

  public selectItem(item: any, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.changeSelectedItems(item);
    this.applyFilters();
  }

  public onMenuOpened() {
    super.onMenuOpened();
    this.isApplied = false;
  }

  public onMenuClosed() {
    super.onMenuClosed();

    if (!this.isApplied) {
      this.setSelectedSet();
    }
  }

  protected setItems(items: any[] | StringAnyMap, selectedIds?: string[]) {
    this.items = items;
    this.selectedIds = selectedIds || [];
    this.setSelectedSet();
  }

  protected filterSource(searchText: string): Observable<any[]> {
    if (typeof this.dataSource === 'function') {
      return this.dataSource({ query: searchText, ...this.searchParams }).pipe(
        map((items) => {
          this.setItems(items, this.selectedIds);
          return this.sortItems(Array.isArray(items) ? items : Object.values(items));
        }),
      );
    }

    const obs = this.dataSource instanceof Observable ? this.dataSource : of({});
    return obs.pipe(
      map((items) => {
        this.setItems(items, this.selectedIds);
        return this.sortItems(this.filterItems(searchText));
      }),
    );
  }
}
